import axios from '@/lib/axios'
import { buildAdminRequestConfig } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  CreateNotificationRecipientInput,
  NotificationRecipient,
  NotificationRecipientIdInput,
  NotificationRecipientVerificationResponse,
  UpdateNotificationRecipientInput,
} from './notification-recipients.types'

/** Every recipient request is scoped to the organization that owns the recipients. */
export interface NotificationRecipientOrganizationScope {
  organizationId: number
}

/** Calls that act on one recipient; the Laravel routes require its id. */
export interface NotificationRecipientTarget extends NotificationRecipientOrganizationScope {
  recipientId: NotificationRecipientIdInput
}

/**
 * The query key entries each fetcher reads. The scoped key builder always sets the organization; it
 * can't mark the other entries required.
 */
export type NotificationRecipientIndexKeyEntries = NotificationRecipientOrganizationScope

export interface NotificationRecipientShowKeyEntries extends NotificationRecipientOrganizationScope {
  recipientId?: number
}

/** Query functions read only the key. */
type NotificationRecipientQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'>

/** `NotificationRecipientController::index()` lists every recipient of the organization and reads no filters. */
export async function getNotificationRecipients({
  queryKey: [{ organizationId }],
}: NotificationRecipientQueryContext<NotificationRecipientIndexKeyEntries>) {
  return (
    (await axios.get<NotificationRecipient[]>(
      '/notification-recipients',
      buildAdminRequestConfig({ organizationId })
    )) || []
  )
}

export async function getNotificationRecipient({
  queryKey: [{ organizationId, recipientId }],
}: NotificationRecipientQueryContext<NotificationRecipientShowKeyEntries>) {
  const response = await axios.get<NotificationRecipient>(
    `/notification-recipients/${recipientId}`,
    buildAdminRequestConfig({ organizationId })
  )
  return response || {}
}

export async function createNotificationRecipient({
  organizationId,
  ...values
}: CreateNotificationRecipientInput & NotificationRecipientOrganizationScope) {
  return axios.post<NotificationRecipient>(
    '/notification-recipients',
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateNotificationRecipient({
  organizationId,
  id,
  ...values
}: UpdateNotificationRecipientInput & NotificationRecipientOrganizationScope) {
  return axios.put<NotificationRecipient>(
    `/notification-recipients/${id}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

/** The API answers 204 with no body. */
export async function deleteNotificationRecipient({
  organizationId,
  recipientId,
}: NotificationRecipientTarget) {
  return axios.delete<void>(
    `/notification-recipients/${recipientId}`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function sendNotificationRecipientVerification({
  organizationId,
  recipientId,
}: NotificationRecipientTarget) {
  return axios.post<NotificationRecipientVerificationResponse>(
    `/notification-recipients/${recipientId}/send-verification`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}
